import React from 'react';
import Icon from 'components/AppIcon';
import { useNotifications } from '../../contexts/NotificationContext';

const TYPE_CONFIG = {
  transaction: { icon: 'Wallet', color: '#00E5FF', bg: 'rgba(0,229,255,0.12)' },
  fraud: { icon: 'ShieldAlert', color: '#FF4444', bg: 'rgba(255,68,68,0.12)' },
  partner: { icon: 'Building2', color: '#7B2FBE', bg: 'rgba(123,47,190,0.12)' },
  subscription: { icon: 'Crown', color: '#FFD700', bg: 'rgba(255,215,0,0.12)' },
};

function getSeverityBorder(type, severity) {
  if (type === 'fraud') {
    if (severity === 'critical' || severity === 'high') return 'rgba(255,68,68,0.6)';
    if (severity === 'medium') return 'rgba(255,140,0,0.6)';
    return 'rgba(255,215,0,0.4)';
  }
  return TYPE_CONFIG?.[type]?.color ? `${TYPE_CONFIG?.[type]?.color}60` : 'rgba(0,229,255,0.4)';
}

function ToastItem({ toast, onDismiss }) {
  const config = TYPE_CONFIG?.[toast?.type] || TYPE_CONFIG?.transaction;
  const isPersistent = toast?.severity === 'high' || toast?.severity === 'critical';
  const borderColor = getSeverityBorder(toast?.type, toast?.severity);

  return (
    <div
      className="flex items-start gap-3 p-3 rounded-xl w-[320px] max-w-[calc(100vw-2rem)]"
      style={{
        background: 'rgba(13,27,42,0.97)',
        border: `1px solid ${borderColor}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
        backdropFilter: 'blur(16px)',
        animation: 'slideInToast 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
        style={{ background: config?.bg }}
      >
        <Icon name={config?.icon} size={15} color={config?.color} strokeWidth={2} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#F8FAFC] leading-tight truncate">
          {toast?.title}
        </p>
        <p className="text-xs text-[#94A3B8] mt-0.5 line-clamp-2 leading-relaxed">
          {toast?.message}
        </p>
        {isPersistent && (
          <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-medium text-[#FF4444]">
            <Icon name="AlertTriangle" size={10} color="#FF4444" strokeWidth={2.5} />
            Requires attention
          </span>
        )}
      </div>
      {/* Dismiss */}
      <button
        onClick={() => onDismiss(toast?.toastId)}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[#64748B] hover:text-[#F8FAFC] hover:bg-[rgba(255,255,255,0.1)] transition-colors"
        aria-label="Dismiss notification"
      >
        <Icon name="X" size={12} strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default function ToastNotifications() {
  const { toasts, dismissToast } = useNotifications();

  if (!toasts?.length) return null;

  return (
    <div
      className="fixed bottom-6 right-4 z-[300] flex flex-col gap-2 items-end"
      aria-label="Notification toasts"
    >
      {toasts?.map(toast => (
        <ToastItem
          key={toast?.toastId}
          toast={toast}
          onDismiss={dismissToast}
        />
      ))}
    </div>
  );
}
